import { useRef, useState } from 'react';

import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { cloneDeep, isEqual } from 'lodash-es';

import { Button } from '@components/button';
import { Card } from '@components/card';
import { createForm, useCreateFormStore } from '@components/form';
import { FormError } from '@components/form-error';
import { IconAddPlus } from '@components/icons/add-plus';
import { IconArrowLeft } from '@components/icons/arrow-left';
import { IconInternalTasklist } from '@components/icons/internal-tasklist';
import { IconQuestion } from '@components/icons/question';
import { IconSelectMultiple } from '@components/icons/select-multiple';
import { IconSelectSingle } from '@components/icons/select-single';
import { CloseButton, hideSidebar, Sidebar, useSidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { CareerSiteLanguageType } from '@locale/locale-store';
import { useScopedTranslate } from '@locale/translate';
import { uuid } from '@utils/uuid';

import { FLAG_MAPPER_SMALL } from 'components/iconMappers';
import { JobOption, JobQuestionMultiLanguage } from 'types/jobs';
import { MultiLanguageTextProps } from 'types/languages';

import { DraggableOption, OptionCard } from './draggable-option';
import { useQuestionsidebar } from './question-type-sidebar';

export const useSelectSidebar = () => {
  const sidebar = useSidebar(SelectSidebar, {
    variant: 'LARGE',
  });
  return sidebar;
};

const Form = createForm();
export type DraggingState = {
  id: number;
  order: number;
  index: number;
} | null;

type SelectsidebarType = 'dropdown' | 'multidropdown';

type SelectSidebarProps =
  | {
      variant: SelectsidebarType;
      languages: CareerSiteLanguageType[];
      question?: never;
      addQuestion?: (question: JobQuestionMultiLanguage) => void;
      editQuestion?: never;
    }
  | {
      variant: SelectsidebarType;
      languages: CareerSiteLanguageType[];
      question?: JobQuestionMultiLanguage;
      addQuestion?: never;
      editQuestion?: (editedQuestion: JobQuestionMultiLanguage) => void;
    };

const SelectSidebar = (props: SelectSidebarProps) => {
  const { variant, languages, question, addQuestion, editQuestion } = props;
  const { t } = useScopedTranslate(SelectSidebarLocale);
  const showQuestionSidebar = useQuestionsidebar();
  const formStore = useCreateFormStore();

  const [options, setOptions] = useState<Array<JobOption>>(
    question?.options ? question.options : []
  );
  const [noOptionsError, setNoOptionsError] = useState<boolean>(false);
  const [draggingItem, setDraggingItem] = useState<DraggingState>(null);
  const [displayMissingIds, setDisplayMissingIds] = useState<string[]>();
  const [newOptionId, setNewOptionId] = useState<string | null>(null);

  const orginalOptions = useRef<Array<JobOption>>(
    question?.options ? cloneDeep(question.options) : []
  );

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 1,
      },
    })
  );

  const isEdit = Boolean(editQuestion);

  const draggingOption = draggingItem ? options[draggingItem.index] : null;
  const optionIds = options.map((option) => String(option.value));

  const isDirtyForm = formStore.useIsDirty();
  const isDirtyOptions = !isEqual(orginalOptions.current, options);

  const isDirty = isDirtyForm || isDirtyOptions;

  const onDragStart = (event: DragStartEvent) => {
    const { active } = event;
    const order = active.data.current?.order;
    setDraggingItem({
      order: order,
      index: active.data.current?.sortable.index,
      id: active.data.current?.id,
    });
  };

  const onDragEnd = async (event: DragEndEvent) => {
    if (!draggingItem) return;
    const { active, over } = event;
    setDraggingItem(null);
    if (!over || active.id === over.id) return;
    const overIndex = optionIds.indexOf(over.id.toString());
    const activeIndex = optionIds.indexOf(active.id.toString());
    changeOptionOrder(activeIndex, overIndex);
  };

  const onDragCancel = () => {
    setDraggingItem(null);
  };

  const editOption = (index: number, option_display: MultiLanguageTextProps) => {
    const wasNew = newOptionId === options[index].value;
    const newOptions = cloneDeep(options);
    newOptions[index].display = option_display;
    if (displayMissingIds?.includes(newOptions[index].value)) {
      const newMissingIds = displayMissingIds.filter((id) => id !== newOptions[index].value);
      setDisplayMissingIds(newMissingIds);
    }
    setNewOptionId(null);
    setOptions(newOptions);
    if (wasNew) {
      handleAddOption(newOptions);
    }
  };

  const removeOption = (index: number) => {
    const newOptions = [...options];
    newOptions.splice(index, 1);
    if (displayMissingIds?.includes(newOptions[index].value)) {
      const newMissingIds = displayMissingIds.filter((id) => id !== newOptions[index].value);
      setDisplayMissingIds(newMissingIds);
    }
    setNewOptionId(null);
    setOptions(newOptions);
  };

  const changeOptionOrder = (activeIndex: number, overIndex: number) => {
    const newOptions = [...options];
    const [removed] = newOptions.splice(activeIndex, 1);
    newOptions.splice(overIndex, 0, removed);
    setOptions(newOptions);
  };

  const handleAddOption = (newOptions?: JobOption[]) => {
    const newId = uuid();
    let newDisplay: MultiLanguageTextProps = {};
    languages.forEach((language) => {
      newDisplay[language] = '';
    });
    const newOption = {
      value: newId,
      display: newDisplay,
    };
    const currentOptions = newOptions ?? cloneDeep(options);
    currentOptions.push(newOption);
    setNewOptionId(newId);
    setOptions(currentOptions);
  };

  const handleSave = (questionTitle: MultiLanguageTextProps) => {
    if (options.length === 0) {
      setNoOptionsError(true);
      return;
    }
    if (options.some((option) => languages.some((language) => option.display[language] === ''))) {
      setDisplayMissingIds(
        options
          .filter((option) => languages.some((language) => option.display[language] === ''))
          .map((option) => option.value)
      );
      return;
    }
    if (question) {
      let editedQuestion = question;
      editedQuestion.options = options;
      editedQuestion.title = questionTitle;
      editQuestion?.(editedQuestion);
    } else {
      const newQuestion: JobQuestionMultiLanguage = {
        id: uuid(),
        options: options,
        title: questionTitle,
        type: variant,
        order: 0,
        required: false,
        hidden: false,
      };
      addQuestion?.(newQuestion);
    }
    hideSidebar();
  };

  const addOptionDisabled = newOptionId !== null;

  const resetForm = () => {
    formStore.actions.resetStore();
    setOptions(question?.options ?? []);
  };

  return (
    <Sidebar>
      <Sidebar.Header>
        <Stack orientation="horizontal" justify="space-between" align="center">
          <Stack space={2} orientation="horizontal" align="center">
            <IconQuestion color="purple50" />
            <Sidebar.HeaderTitle title={isEdit ? t('titleEdit') : t('title')} />
          </Stack>
          <Stack space={2} orientation="horizontal" align="center">
            {!isEdit && (
              <Button
                iconLeft={<IconArrowLeft />}
                onClick={() => showQuestionSidebar({ languages, addQuestion })}
                text={t('back')}
                variant="ghost"
              />
            )}
            <CloseButton />
          </Stack>
        </Stack>
      </Sidebar.Header>
      <Sidebar.Content>
        <Form store={formStore} onSubmit={(data) => handleSave(data)}>
          <Stack space={6}>
            <Stack>
              <Stack space={4} orientation="horizontal" align="center">
                {variant === 'dropdown' ? <IconSelectSingle /> : <IconSelectMultiple />}
                <Text
                  text={variant === 'dropdown' ? t('singleSelect') : t('multiSelect')}
                  font="bold18"
                />
              </Stack>
              <Text
                text={
                  variant === 'dropdown'
                    ? t('singleSelectDescription')
                    : t('multiSelectDescription')
                }
                font="regular14"
                color="purple80"
              />
            </Stack>
            <Stack space={4} justify="center">
              {languages.map((language) => (
                <Form.TextField
                  key={`${language}`}
                  id={`${language}`}
                  labelIcon={languages.length > 1 ? FLAG_MAPPER_SMALL[language] : undefined}
                  label={
                    languages.length > 1
                      ? t('question') + ' • ' + language.toUpperCase()
                      : t('question')
                  }
                  defaultValue={question?.title[language]}
                  validators={['required']}
                />
              ))}
              <Stack space={1}>
                <Text text={t('options')} font="semibold14" />
                <Stack space={2}>
                  {options.length === 0 ? (
                    <Stack space={1}>
                      <Card variant="gray" space={6}>
                        <Stack space={4} orientation="horizontal">
                          <IconInternalTasklist />
                          <Text text={t('noOptions')} color="purple80" />
                        </Stack>
                      </Card>
                      {noOptionsError && <FormError text={t('optionsError')} />}
                    </Stack>
                  ) : (
                    <>
                      <DndContext
                        sensors={sensors}
                        onDragStart={onDragStart}
                        onDragEnd={onDragEnd}
                        onDragCancel={onDragCancel}
                      >
                        <DragOverlay>
                          {draggingOption && (
                            <OptionCard
                              option={draggingOption}
                              index={Number(draggingItem?.index)}
                              languages={languages}
                            />
                          )}
                        </DragOverlay>
                        <SortableContext items={optionIds} strategy={verticalListSortingStrategy}>
                          {options.map((option, index) => (
                            <Stack space={2} key={option.value}>
                              <DraggableOption
                                option={option}
                                index={index}
                                editOption={editOption}
                                removeOption={() => removeOption(index)}
                                languages={languages}
                                isDragging={draggingOption?.value === option.value}
                                newId={newOptionId}
                                displayMissing={
                                  displayMissingIds
                                    ? displayMissingIds.includes(option.value)
                                    : false
                                }
                              />
                            </Stack>
                          ))}
                        </SortableContext>
                      </DndContext>
                    </>
                  )}
                  <Stack align="end">
                    <Button
                      variant="secondary"
                      iconLeft={<IconAddPlus />}
                      text={t('addOption')}
                      onClick={() => handleAddOption()}
                      isDisabled={addOptionDisabled}
                    />
                  </Stack>
                </Stack>
              </Stack>
            </Stack>
            <Stack space={2} orientation="horizontal" justify="end">
              <Form.Actions>
                <Button
                  variant="secondary"
                  text={t('cancel')}
                  onClick={() => {
                    resetForm();
                    hideSidebar();
                  }}
                />
                <Form.Submit
                  variant="primary-blue"
                  type="submit"
                  text={question ? t('save') : t('title')}
                  isDisabled={!isDirty || newOptionId !== null}
                />
              </Form.Actions>
            </Stack>
          </Stack>
        </Form>
      </Sidebar.Content>
    </Sidebar>
  );
};

const SelectSidebarLocale = {
  en: {
    title: 'Add question',
    titleEdit: 'Edit question',
    singleSelect: 'Select - Single select',
    singleSelectDescription:
      'Dropdown with single select options where only one answer is applicable',
    multiSelect: 'Select - Multi select',
    multiSelectDescription: 'Dropdown field where multiple answers can apply',
    question: 'Question',
    questionError: 'No question added',
    options: 'Options',
    addOption: 'Add option',
    noOptions: 'No options added',
    optionsError: 'Please add options',
    back: 'Back',
    cancel: 'Cancel',
    add: 'Add',
    update: 'Update',
    save: 'Save',
  },
  is: {
    title: 'Bæta við spurningu',
    titleEdit: 'Breyta spurningu',
    singleSelect: 'Fellilisti - Eitt val',
    singleSelectDescription: 'Fellilisti þar sem umsækjandi getur valið einn valmöguleika',
    multiSelect: 'Fellilisti - Fjölval',
    multiSelectDescription:
      'Fellilisti þar sem umsækjandi getur valið einn eða fleiri valmöguleika',
    question: 'Spurning',
    questionError: 'Spurningu vantar',
    options: 'Valmöguleikar',
    addOption: 'Bæta við valmöguleika',
    noOptions: 'Engir valmöguleikar',
    optionsError: 'Engir valmöguleikar',
    back: 'Til baka',
    cancel: 'Hætta við',
    add: 'Bæta við',
    update: 'Uppfæra',
    save: 'Vista',
  },
};
